declare const __SPARELYNK_BUILD_ID__: string;

const BUILD_VERSION_URL = "/version.json";
const SERVICE_WORKER_URL = "/sw.js";
const UPDATE_CHECK_INTERVAL_MS = 15_000;
const UPDATE_QUERY_KEY = "__sparelynk_update";
const CACHE_BUST_QUERY_KEY = "__sparelynk_bust";
const HARD_REFRESH_CLEANUP_TIMEOUT_MS = 1_500;
const SERVICE_WORKER_READY_TIMEOUT_MS = 10_000;
const UPDATE_STATE_KEY = "sparelynk:available-update";
const UPDATE_SNOOZE_KEY = "sparelynk:update-snooze";
const UPDATE_REFRESH_ATTEMPT_KEY = "sparelynk:update-refresh-attempt";
const UPDATE_SNOOZE_MS = 5 * 60_000;
const UPDATE_CHANNEL_NAME = "sparelynk-app-update";
const BUILD_META_SELECTOR = 'meta[name="sparelynk-build-id"]';

export const APP_UPDATE_AVAILABLE_EVENT = "sparelynk:app-update-available";

export type AppUpdateSource =
  | "version"
  | "service-worker"
  | "broadcast"
  | "storage";

export type AppUpdateAvailableDetail = {
  buildId: string;
  detectedAt: number;
  source: AppUpdateSource;
};

type StoredUpdateState = Pick<AppUpdateAvailableDetail, "buildId" | "detectedAt">;
type StoredSnoozeState = { buildId: string; until: number };
type StoredRefreshAttempt = {
  buildId: string;
  fromBuildId: string;
  attemptedAt: number;
};

let refreshInProgress = false;
let updateCheckInProgress: Promise<void> | null = null;
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
let updateBroadcastChannel: BroadcastChannel | null = null;
let uninstallAutomaticUpdates: (() => void) | null = null;
let pendingUpdate: StoredUpdateState | null = null;

function stripUpdateQueryParameters(): void {
  const url = new URL(window.location.href);
  let changed = false;

  for (const key of [UPDATE_QUERY_KEY, CACHE_BUST_QUERY_KEY]) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  if (changed) {
    window.history.replaceState(window.history.state, "", url.toString());
  }
}

function createCacheBustedUrl(pathname: string): string {
  const url = new URL(pathname, window.location.origin);
  url.searchParams.set(CACHE_BUST_QUERY_KEY, `${Date.now()}-${Math.random()}`);
  return url.toString();
}

function safeReadJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function safeWriteJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be disabled; the in-memory event still keeps this tab current.
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Storage can be disabled in restricted browser modes.
  }
}

function safeReadSessionJson<T>(key: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function safeWriteSessionJson(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Session storage can be disabled; update detection still works without it.
  }
}

function safeRemoveSession(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Session storage can be disabled in restricted browser modes.
  }
}

function readRefreshAttempt(): StoredRefreshAttempt | null {
  const stored = safeReadSessionJson<StoredRefreshAttempt>(UPDATE_REFRESH_ATTEMPT_KEY);
  if (!stored) return null;

  const buildId = String(stored.buildId ?? "").trim();
  const fromBuildId = String(stored.fromBuildId ?? "").trim();
  const attemptedAt = Number(stored.attemptedAt ?? 0);

  if (!buildId || !fromBuildId) {
    safeRemoveSession(UPDATE_REFRESH_ATTEMPT_KEY);
    return null;
  }

  return {
    buildId,
    fromBuildId,
    attemptedAt:
      Number.isFinite(attemptedAt) && attemptedAt > 0 ? attemptedAt : Date.now(),
  };
}

function reconcileRefreshAttempt(): void {
  const attempt = readRefreshAttempt();
  if (!attempt) return;

  if (
    attempt.buildId === __SPARELYNK_BUILD_ID__ ||
    attempt.fromBuildId !== __SPARELYNK_BUILD_ID__
  ) {
    safeRemoveSession(UPDATE_REFRESH_ATTEMPT_KEY);
  }
}

function wasRefreshAttemptedForBuild(buildId: string): boolean {
  const attempt = readRefreshAttempt();
  return Boolean(
    attempt &&
      attempt.buildId === buildId &&
      attempt.fromBuildId === __SPARELYNK_BUILD_ID__,
  );
}

function readStoredUpdate(): StoredUpdateState | null {
  const stored = safeReadJson<StoredUpdateState>(UPDATE_STATE_KEY);
  if (!stored) return null;

  const buildId = String(stored.buildId ?? "").trim();
  const detectedAt = Number(stored.detectedAt ?? 0);

  if (
    !buildId ||
    buildId === __SPARELYNK_BUILD_ID__ ||
    wasRefreshAttemptedForBuild(buildId)
  ) {
    safeRemove(UPDATE_STATE_KEY);
    return null;
  }

  return {
    buildId,
    detectedAt: Number.isFinite(detectedAt) && detectedAt > 0 ? detectedAt : Date.now(),
  };
}

function isUpdateSnoozed(buildId: string): boolean {
  const snooze = safeReadJson<StoredSnoozeState>(UPDATE_SNOOZE_KEY);
  if (!snooze || String(snooze.buildId ?? "") !== buildId) {
    return false;
  }

  const until = Number(snooze.until ?? 0);
  if (!Number.isFinite(until) || until <= Date.now()) {
    safeRemove(UPDATE_SNOOZE_KEY);
    return false;
  }

  return true;
}

function dispatchUpdateAvailable(
  update: StoredUpdateState,
  source: AppUpdateSource,
): void {
  if (
    refreshInProgress ||
    isUpdateSnoozed(update.buildId) ||
    wasRefreshAttemptedForBuild(update.buildId)
  ) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<AppUpdateAvailableDetail>(APP_UPDATE_AVAILABLE_EVENT, {
      detail: { ...update, source },
    }),
  );
}

function rememberAvailableUpdate(
  buildId: string,
  source: AppUpdateSource,
  broadcast = true,
): void {
  const normalizedBuildId = buildId.trim();
  if (
    refreshInProgress ||
    !normalizedBuildId ||
    normalizedBuildId === __SPARELYNK_BUILD_ID__ ||
    wasRefreshAttemptedForBuild(normalizedBuildId)
  ) {
    return;
  }

  const existing = pendingUpdate ?? readStoredUpdate();
  const update: StoredUpdateState =
    existing?.buildId === normalizedBuildId
      ? existing
      : { buildId: normalizedBuildId, detectedAt: Date.now() };

  pendingUpdate = update;
  safeWriteJson(UPDATE_STATE_KEY, update);
  dispatchUpdateAvailable(update, source);

  if (broadcast) {
    try {
      updateBroadcastChannel?.postMessage(update);
    } catch {
      // BroadcastChannel is optional; storage events remain a cross-tab fallback.
    }
  }
}

export function getPendingAppUpdate(): AppUpdateAvailableDetail | null {
  if (typeof window === "undefined") {
    return null;
  }

  const update = pendingUpdate ?? readStoredUpdate();
  if (
    !update ||
    isUpdateSnoozed(update.buildId) ||
    wasRefreshAttemptedForBuild(update.buildId)
  ) {
    return null;
  }

  pendingUpdate = update;
  return { ...update, source: "storage" };
}

export function snoozeAppUpdate(buildId: string): void {
  if (typeof window === "undefined") {
    return;
  }

  safeWriteJson(UPDATE_SNOOZE_KEY, {
    buildId,
    until: Date.now() + UPDATE_SNOOZE_MS,
  } satisfies StoredSnoozeState);
}

async function clearBrowserCachesForRefresh(): Promise<void> {
  if (!("caches" in window)) {
    return;
  }

  const cacheNames = await window.caches.keys();
  await Promise.allSettled(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
}

async function unregisterServiceWorkersForRefresh(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.allSettled(registrations.map((registration) => registration.unregister()));
  serviceWorkerRegistration = null;
}

function waitForRefreshTimeout(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, HARD_REFRESH_CLEANUP_TIMEOUT_MS);
  });
}

export async function clearCacheAndHardRefresh(
  nextBuildId: string = String(Date.now()),
): Promise<void> {
  if (refreshInProgress) {
    return;
  }

  const normalizedBuildId = nextBuildId.trim() || String(Date.now());

  refreshInProgress = true;
  safeWriteSessionJson(UPDATE_REFRESH_ATTEMPT_KEY, {
    buildId: normalizedBuildId,
    fromBuildId: __SPARELYNK_BUILD_ID__,
    attemptedAt: Date.now(),
  } satisfies StoredRefreshAttempt);
  safeRemove(UPDATE_SNOOZE_KEY);
  safeRemove(UPDATE_STATE_KEY);
  pendingUpdate = null;

  try {
    await Promise.race([
      Promise.allSettled([
        clearBrowserCachesForRefresh(),
        unregisterServiceWorkersForRefresh(),
      ]).then(() => undefined),
      waitForRefreshTimeout(),
    ]);
  } finally {
    const url = new URL(window.location.href);
    url.searchParams.set(UPDATE_QUERY_KEY, normalizedBuildId);
    url.searchParams.set(CACHE_BUST_QUERY_KEY, `${Date.now()}-${Math.random()}`);
    window.location.replace(url.toString());
  }
}

async function fetchLatestBuildId(): Promise<string | null> {
  try {
    const response = await fetch(createCacheBustedUrl(BUILD_VERSION_URL), {
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        "cache-control": "no-cache, no-store, max-age=0, must-revalidate",
        pragma: "no-cache",
      },
    });

    if (!response.ok) {
      return null;
    }

    const payload = (await response.json()) as { buildId?: unknown };
    const buildId = String(payload.buildId ?? "").trim();
    return buildId || null;
  } catch {
    return null;
  }
}

async function fetchDeployedDocumentBuildId(): Promise<string | null> {
  try {
    const response = await fetch(createCacheBustedUrl("/"), {
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        accept: "text/html",
        "cache-control": "no-cache, no-store, max-age=0, must-revalidate",
        pragma: "no-cache",
      },
    });

    if (!response.ok) {
      return null;
    }

    const html = await response.text();
    const documentRoot = new DOMParser().parseFromString(html, "text/html");
    const buildId =
      documentRoot.querySelector(BUILD_META_SELECTOR)?.getAttribute("content")?.trim() ?? "";

    return buildId || null;
  } catch {
    return null;
  }
}

export function checkForAppUpdate(
  source: AppUpdateSource = "version",
): Promise<void> {
  if (refreshInProgress) {
    return Promise.resolve();
  }

  if (updateCheckInProgress) {
    return updateCheckInProgress;
  }

  updateCheckInProgress = (async () => {
    const latestBuildId = await fetchLatestBuildId();

    if (!latestBuildId) {
      return;
    }

    if (latestBuildId === __SPARELYNK_BUILD_ID__) {
      pendingUpdate = null;
      safeRemove(UPDATE_STATE_KEY);
      safeRemove(UPDATE_SNOOZE_KEY);
      safeRemoveSession(UPDATE_REFRESH_ATTEMPT_KEY);
      return;
    }

    if (wasRefreshAttemptedForBuild(latestBuildId)) {
      pendingUpdate = null;
      safeRemove(UPDATE_STATE_KEY);
      return;
    }

    // version.json can become visible slightly before the new HTML during a deployment.
    // Only prompt once both resources point to the same deployed build.
    const deployedDocumentBuildId = await fetchDeployedDocumentBuildId();
    if (deployedDocumentBuildId !== latestBuildId) {
      return;
    }

    rememberAvailableUpdate(latestBuildId, source);
  })().finally(() => {
    updateCheckInProgress = null;
  });

  return updateCheckInProgress;
}

function watchInstallingWorker(worker: ServiceWorker | null): void {
  if (!worker) return;

  const handleStateChange = () => {
    if (worker.state === "installed" && navigator.serviceWorker.controller) {
      void checkForAppUpdate("service-worker");
    }
  };

  worker.addEventListener("statechange", handleStateChange);
}

function withServiceWorkerTimeout<T>(
  promise: Promise<T>,
  message: string,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(
      () => reject(new Error(message)),
      SERVICE_WORKER_READY_TIMEOUT_MS,
    );

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

function expectedServiceWorkerScope(): string {
  return new URL("/", window.location.origin).href;
}

function expectedServiceWorkerScriptUrl(): string {
  return new URL(SERVICE_WORKER_URL, window.location.origin).href;
}

function registrationUsesAppServiceWorker(
  registration: ServiceWorkerRegistration,
): boolean {
  const expectedScriptUrl = expectedServiceWorkerScriptUrl();
  return [registration.active, registration.waiting, registration.installing].some(
    (worker) => worker?.scriptURL === expectedScriptUrl,
  );
}

function registrationHasActivatedAppServiceWorker(
  registration: ServiceWorkerRegistration,
): boolean {
  return (
    registration.scope === expectedServiceWorkerScope() &&
    registration.active?.state === "activated" &&
    registration.active.scriptURL === expectedServiceWorkerScriptUrl()
  );
}

async function ensureAppServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported by this browser");
  }

  const expectedScope = expectedServiceWorkerScope();
  const currentRegistration = await navigator.serviceWorker.getRegistration("/");
  let registration = currentRegistration;

  if (
    !registration ||
    registration.scope !== expectedScope ||
    !registrationUsesAppServiceWorker(registration)
  ) {
    registration = await withServiceWorkerTimeout(
      navigator.serviceWorker.register(SERVICE_WORKER_URL, {
        scope: "/",
        updateViaCache: "none",
      }),
      "Timed out registering the SpareLynk service worker",
    );
  }

  serviceWorkerRegistration = registration;
  return registration;
}

function waitForActivatedServiceWorker(
  registration: ServiceWorkerRegistration,
): Promise<ServiceWorkerRegistration> {
  if (registrationHasActivatedAppServiceWorker(registration)) {
    return Promise.resolve(registration);
  }

  const worker = registration.installing ?? registration.waiting ?? registration.active;
  if (!worker) {
    return Promise.reject(new Error("The app service worker is not available"));
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      worker.removeEventListener("statechange", handleStateChange);
      reject(new Error("Timed out waiting for the app service worker to activate"));
    }, SERVICE_WORKER_READY_TIMEOUT_MS);

    const handleStateChange = () => {
      if (registrationHasActivatedAppServiceWorker(registration)) {
        window.clearTimeout(timeoutId);
        worker.removeEventListener("statechange", handleStateChange);
        resolve(registration);
      } else if (worker.state === "redundant") {
        window.clearTimeout(timeoutId);
        worker.removeEventListener("statechange", handleStateChange);
        reject(new Error("The app service worker became redundant before activation"));
      }
    };

    worker.addEventListener("statechange", handleStateChange);
    handleStateChange();
  });
}

function waitForServiceWorkerReady(): Promise<ServiceWorkerRegistration> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(
      () => reject(new Error("Timed out waiting for the app service worker to become ready")),
      SERVICE_WORKER_READY_TIMEOUT_MS,
    );

    navigator.serviceWorker.ready.then(
      (registration) => {
        window.clearTimeout(timeoutId);
        resolve(registration);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

async function waitForReadyAppServiceWorker(
  registration: ServiceWorkerRegistration,
): Promise<ServiceWorkerRegistration> {
  await waitForActivatedServiceWorker(registration);
  const readyRegistration = await waitForServiceWorkerReady();

  if (!registrationHasActivatedAppServiceWorker(readyRegistration)) {
    throw new Error("The ready service worker is not the SpareLynk root service worker");
  }

  serviceWorkerRegistration = readyRegistration;
  return readyRegistration;
}

export async function getAppServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (typeof window === "undefined") {
    throw new Error("The app service worker is only available in the browser");
  }

  const registration = await ensureAppServiceWorkerRegistration();
  return waitForReadyAppServiceWorker(registration);
}

export async function recoverAppServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (typeof window === "undefined") {
    throw new Error("The app service worker is only available in the browser");
  }
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported by this browser");
  }
  if (refreshInProgress) {
    throw new Error("The app is refreshing and the service worker is being replaced");
  }

  serviceWorkerRegistration = null;
  const registration = await withServiceWorkerTimeout(
    navigator.serviceWorker.register(SERVICE_WORKER_URL, {
      scope: "/",
      updateViaCache: "none",
    }),
    "Timed out re-registering the SpareLynk service worker",
  );
  serviceWorkerRegistration = registration;

  try {
    await withServiceWorkerTimeout(
      registration.update(),
      "Timed out updating the SpareLynk service worker during recovery",
    );
  } catch {
    // A fresh register() is enough to re-establish the root registration.
    // update() is best-effort because offline/cache state can make it fail.
  }

  return waitForReadyAppServiceWorker(registration);
}

async function requestServiceWorkerUpdate(): Promise<void> {
  if (!serviceWorkerRegistration || !navigator.onLine || refreshInProgress) {
    return;
  }

  try {
    await fetch(createCacheBustedUrl(SERVICE_WORKER_URL), {
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        "cache-control": "no-cache, no-store, max-age=0, must-revalidate",
        pragma: "no-cache",
      },
    });
    await serviceWorkerRegistration.update();
  } catch {
    // version.json remains the authoritative update fallback.
  }
}

async function registerServiceWorker(): Promise<() => void> {
  if (!("serviceWorker" in navigator)) {
    return () => undefined;
  }

  try {
    const registration = await ensureAppServiceWorkerRegistration();

    const handleUpdateFound = () => {
      watchInstallingWorker(registration.installing);
    };

    registration.addEventListener("updatefound", handleUpdateFound);
    watchInstallingWorker(registration.installing);

    if (registration.waiting) {
      void checkForAppUpdate("service-worker");
    }

    await requestServiceWorkerUpdate();

    return () => {
      registration.removeEventListener("updatefound", handleUpdateFound);
    };
  } catch (error) {
    console.error("[app-update] service worker registration failed", {
      name: error instanceof Error ? error.name : "UnknownError",
      message: error instanceof Error ? error.message : String(error),
    });
    serviceWorkerRegistration = null;
    return () => undefined;
  }
}

export function installAutomaticAppUpdates(): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  if (uninstallAutomaticUpdates) {
    return uninstallAutomaticUpdates;
  }

  reconcileRefreshAttempt();
  stripUpdateQueryParameters();

  const checkWhenActive = () => {
    if (
      refreshInProgress ||
      document.visibilityState !== "visible" ||
      !navigator.onLine
    ) {
      return;
    }

    const stored = pendingUpdate ?? readStoredUpdate();
    if (stored) {
      pendingUpdate = stored;
      dispatchUpdateAvailable(stored, "storage");
    }

    void checkForAppUpdate();
    void requestServiceWorkerUpdate();
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== UPDATE_STATE_KEY || !event.newValue || refreshInProgress) {
      return;
    }

    const stored = readStoredUpdate();
    if (!stored) return;

    pendingUpdate = stored;
    dispatchUpdateAvailable(stored, "storage");
  };

  if ("BroadcastChannel" in window) {
    try {
      updateBroadcastChannel = new BroadcastChannel(UPDATE_CHANNEL_NAME);
      updateBroadcastChannel.addEventListener("message", (event) => {
        if (refreshInProgress) return;

        const buildId = String((event.data as StoredUpdateState | null)?.buildId ?? "").trim();
        if (buildId) {
          rememberAvailableUpdate(buildId, "broadcast", false);
        }
      });
    } catch {
      updateBroadcastChannel = null;
    }
  }

  const intervalId = window.setInterval(checkWhenActive, UPDATE_CHECK_INTERVAL_MS);

  window.addEventListener("focus", checkWhenActive);
  window.addEventListener("online", checkWhenActive);
  window.addEventListener("pageshow", checkWhenActive);
  window.addEventListener("storage", handleStorage);
  document.addEventListener("visibilitychange", checkWhenActive);

  const handleControllerChange = () => {
    void checkForAppUpdate("service-worker");
  };

  navigator.serviceWorker?.addEventListener("controllerchange", handleControllerChange);

  let stopWatchingServiceWorker = () => undefined;
  void registerServiceWorker().then((cleanup) => {
    stopWatchingServiceWorker = cleanup;
  });

  const stored = readStoredUpdate();
  if (stored) {
    pendingUpdate = stored;
    dispatchUpdateAvailable(stored, "storage");
  }

  void checkForAppUpdate();

  uninstallAutomaticUpdates = () => {
    window.clearInterval(intervalId);
    window.removeEventListener("focus", checkWhenActive);
    window.removeEventListener("online", checkWhenActive);
    window.removeEventListener("pageshow", checkWhenActive);
    window.removeEventListener("storage", handleStorage);
    document.removeEventListener("visibilitychange", checkWhenActive);
    navigator.serviceWorker?.removeEventListener("controllerchange", handleControllerChange);
    stopWatchingServiceWorker();
    updateBroadcastChannel?.close();
    updateBroadcastChannel = null;
    uninstallAutomaticUpdates = null;
  };

  return uninstallAutomaticUpdates;
}
